import { Warning } from '@phosphor-icons/react';
import { CheckCircle } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';

/** Auth inputs — match .pen field-box (rounded-ds-md, type-body). */
export const authInputClassName = 'rounded-ds-md border border-border bg-card px-space-4 py-space-3 text-type-body';

/**
 * Visual scaffolding for auth forms. Pages compose `<AuthField>` with React
 * Hook Form `register(...)` to keep field markup consistent.
 */
export function AuthForm({ onSubmit, children, className }) {
  return (
    <form
      onSubmit={onSubmit}
      className={cn('flex w-full flex-col gap-space-5 short:gap-space-2', className)}
      noValidate
    >
      {children}
    </form>
  );
}

export function AuthField({ id, label, error, hint, children, className }) {
  return (
    <div className={cn('flex flex-col gap-space-2', className)}>
      <label htmlFor={id} className="type-label font-semibold text-foreground">
        {label}
      </label>
      {children}
      {error ? (
        <p className="flex items-start gap-1.5 text-type-body-sm leading-snug text-danger-700">
          <Warning className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p className="text-type-body-sm text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export function AuthFormError({ message }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-space-2 rounded-ds-md bg-danger-tint px-space-4 py-space-3 text-type-body-sm text-danger-700"
    >
      <Warning className="mt-0.5 size-[18px] shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export function AuthFormSuccess({ message }) {
  if (!message) return null;
  return (
    <div
      role="status"
      className="flex items-start gap-space-2 rounded-ds-md bg-success-tint px-space-4 py-space-3 text-type-body-sm text-success-700"
    >
      <CheckCircle className="mt-0.5 size-[18px] shrink-0" weight="fill" aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export function AuthFormHeader({ title, subtitle, chip, className }) {
  return (
    <div className={cn('flex flex-col gap-space-2 text-center md:text-left', className)}>
      {chip ? (
        <span className="type-chip mx-auto inline-flex w-fit rounded-ds-full bg-sage-tint px-space-3 py-space-1 text-sage-on-tint md:mx-0">
          {chip}
        </span>
      ) : null}
      <h1 className="font-display text-type-title-page-lg font-bold tracking-tight text-foreground">
        {title}
      </h1>
      {subtitle ? <p className="text-type-body leading-relaxed text-muted-foreground">{subtitle}</p> : null}
    </div>
  );
}

export function AuthFormFooter({ children, className }) {
  return (
    <p
      className={cn(
        'mt-auto flex flex-wrap items-center justify-center gap-space-1 pt-space-2 text-center text-type-body-sm text-muted-foreground md:mt-0',
        className,
      )}
    >
      {children}
    </p>
  );
}
